import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { partyShellSupportsNativeTvCast } from "../../party/modePartyCards";
import PartyCastQrPanel from "../../party/PartyCastQrPanel";
import PartyCastSourceToggle from "../../party/PartyCastSourceToggle";
import { buildPartyCastRevealUrl } from "../../party/partyCastPayload";
import type { PartyCastPayloadV1 } from "../../party/partyCastPayload";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { PartyPressToReveal } from "../../party/PartyPressToReveal";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import { PartyTopBar } from "../../party/PartyChrome";
import { PartyConfettiCards, PartyMascot } from "../../party/PartyMascot";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import type { PartyShellPalette } from "../../theme/theme";
import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import type { ModeCategoryRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { modeCategoryDisplayName, modeCategoryPrimaryName } from "../../utils/modeCategoryLabels";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import { PartySuggestCategoryDrawer } from "../../party/PartySuggestCategoryDrawer";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import { TRIAL_IMPOSTER_HINTS, TRIAL_IMPOSTER_SECRET_WORD } from "../../party/partyTrialMockData";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

const MODE_SEGMENT = partyShellIdToModePathSegment("imposter");
const CACHE_SHELL = "imposter";

function normalizeImposterContentHints(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === "string")
    .map((s) => s.trim())
    .filter(Boolean);
}

function shuffleIndices(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

type Step =
  | "lobby"
  | "pick_category"
  | "reveal"
  | "questions"
  | "vote"
  | "after_vote"
  | "imposter_guess"
  | "outcome";

type ImposterCacheData = {
  step: Step;
  players: string[];
  newName: string;
  imposterCount: number;
  imposterIndices: number[];
  secretWord: string;
  secretWordHints: string[];
  current: number;
  revealed: boolean;
  crewWordRevealed: boolean;
  votes: Record<number, number[]>;
  voteSelection: number[];
  eliminatedIndices: number[];
  voteTie: boolean;
  finalCrewWins: boolean | null;
  showImposterReveal: boolean;
};

export default function PartyPlayImposter() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const isTrialMode = usePartyPlayTrialMode();
  const partyPal = (useTheme().palette as unknown as { party: PartyShellPalette }).party;
  const savedRoster = usePartySavedRoster();
  const shareModeTitle = usePartyShareModeTitle("imposter", i18n.language?.startsWith("ar") ? "الجاسوس" : "Imposter");
  const [step, setStep] = useState<Step>("lobby");
  const [players, setPlayers] = useState<string[]>([]);
  const [newName, setNewName] = useState("");
  const [imposterCount, setImposterCount] = useState(1);
  const [imposterSet, setImposterSet] = useState<Set<number>>(new Set());
  const [secretWord, setSecretWord] = useState("");
  const [secretWordHints, setSecretWordHints] = useState<string[]>([]);
  const [current, setCurrent] = useState(0);
  const [revealed, setRevealed] = useState(false);
  /** Crew only: second long-press before the secret word is shown (so someone peeking beside the player does not see it instantly). */
  const [crewWordRevealed, setCrewWordRevealed] = useState(false);
  const [votes, setVotes] = useState<Record<number, number[]>>({});
  /** Draft picks for the current voter when casting multiple votes. */
  const [voteSelection, setVoteSelection] = useState<number[]>([]);
  /** Everyone voted out this round (can be several if tied at the cutoff). */
  const [eliminatedIndices, setEliminatedIndices] = useState<number[]>([]);
  const [voteTie, setVoteTie] = useState(false);
  const [finalCrewWins, setFinalCrewWins] = useState<boolean | null>(null);
  const [showImposterReveal, setShowImposterReveal] = useState(false);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const completionTrackedRef = useRef(false);
  /** Second screen: QR encodes URL with secret text (no server). Hidden when this shell has native TV cast. */
  const [castByQr, setCastByQr] = useState(false);
  const [suggestCategoryOpen, setSuggestCategoryOpen] = useState(false);
  const nativeTvCast = partyShellSupportsNativeTvCast(CACHE_SHELL);

  const [cacheEnvelope] = useState(() => readPartyGameCache<ImposterCacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const cachedImposterProgress = (() => {
    if (!cacheEnvelope?.data) return false;
    const d = cacheEnvelope.data;
    if (d.step === "outcome") return false;
    if (d.step !== "lobby") return true;
    return d.players.length > 0 || d.newName.trim().length > 0;
  })();
  const showResumePrompt = Boolean(cachedImposterProgress && !resumeDismissed);

  const categoriesQ = useQuery({
    queryKey: ["modes", MODE_SEGMENT, "categories"],
    queryFn: () => apiClient.getModeCategories(MODE_SEGMENT),
    enabled: step === "pick_category" && !isTrialMode,
  });

  useLayoutEffect(() => {
    if (isTrialMode && step === "pick_category") setStep("lobby");
  }, [isTrialMode, step]);

  const startMut = useMutation({
    mutationFn: ({ categoryId }: { categoryId: number; category_names: string[] }) =>
      apiClient.postImposterStart(categoryId),
    onSuccess: (data: any, variables) => {
      const w = data?.content?.word ?? data?.content?.word_ar;
      setSecretWord(typeof w === "string" ? w : String(w ?? ""));
      setSecretWordHints(normalizeImposterContentHints(data?.content?.hints));
      setCurrent(0);
      setRevealed(false);
      setCrewWordRevealed(false);
      setStep("reveal");
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "imposter",
        category_names: variables.category_names,
        number_of_players: players.length,
        trial: false,
        mode_name_en: "Imposter",
        imposter_count: imposterCount,
      });
    },
  });

  const isImposter = (i: number) => imposterSet.has(i);

  const addPlayer = () => {
    const n = newName.trim();
    if (!n) return;
    setPlayers((p) => [...p, n]);
    setNewName("");
    savedRoster.recordPlayerAdded(n);
  };
  const removePlayer = (i: number) => setPlayers((p) => p.filter((_, j) => j !== i));

  const categoryLabel = (c: ModeCategoryRow) => modeCategoryDisplayName(c, i18n.language);

  const questionPairs = useMemo(() => {
    if (players.length < 2) return [];
    const pairs: { asker: string; target: string }[] = [];
    for (let i = 0; i < players.length; i++) {
      const j = (i + 1) % players.length;
      pairs.push({ asker: players[i], target: players[j] });
    }
    return pairs;
  }, [players]);

  /** Each voter names this many suspects (capped by how many others exist). */
  const votesPerVoter = useMemo(() => {
    if (players.length < 2) return 1;
    return Math.min(imposterCount, players.length - 1);
  }, [imposterCount, players.length]);

  useEffect(() => {
    if (step !== "vote") return;
    setVoteSelection([]);
  }, [step, current]);

  const goLobbyToCategory = () => {
    savedRoster.recordPlayersBatch(players);
    const cap = Math.min(imposterCount, Math.max(1, players.length - 1));
    const sh = shuffleIndices(players.length);
    setImposterSet(new Set(sh.slice(0, cap)));
    setStep("pick_category");
  };

  const startImposterTrial = () => {
    savedRoster.recordPlayersBatch(players);
    const cap = Math.min(imposterCount, Math.max(1, players.length - 1));
    const sh = shuffleIndices(players.length);
    setImposterSet(new Set(sh.slice(0, cap)));
    setSecretWord(TRIAL_IMPOSTER_SECRET_WORD);
    setSecretWordHints([...TRIAL_IMPOSTER_HINTS]);
    setCurrent(0);
    setRevealed(false);
    setCrewWordRevealed(false);
    setStep("reveal");
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    trackPartyGameStarted({
      shell_id: "imposter",
      category_names: ["trial_sample"],
      number_of_players: players.length,
      trial: true,
      mode_name_en: "Imposter",
      imposter_count: imposterCount,
    });
  };

  useEffect(() => {
    if (step === "outcome") {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: ImposterCacheData = {
        step,
        players,
        newName,
        imposterCount,
        imposterIndices: Array.from(imposterSet),
        secretWord,
        secretWordHints,
        current,
        revealed,
        crewWordRevealed,
        votes,
        voteSelection,
        eliminatedIndices,
        voteTie,
        finalCrewWins,
        showImposterReveal,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 450);
    return () => window.clearTimeout(id);
  }, [
    step,
    players,
    newName,
    imposterCount,
    imposterSet,
    secretWord,
    secretWordHints,
    current,
    revealed,
    crewWordRevealed,
    votes,
    voteSelection,
    eliminatedIndices,
    voteTie,
    finalCrewWins,
    showImposterReveal,
  ]);

  useEffect(() => {
    if (step !== "outcome" || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "imposter",
      trial: isTrialMode,
      mode_name_en: "Imposter",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [gameStartedAtMs, isTrialMode, step]);

  const applyImposterCache = useCallback((data: ImposterCacheData) => {
    const st = data.step;
    const validSteps: Step[] = [
      "lobby",
      "pick_category",
      "reveal",
      "questions",
      "vote",
      "after_vote",
      "imposter_guess",
      "outcome",
    ];
    setStep(validSteps.includes(st) && st !== "outcome" ? st : "lobby");
    setPlayers(Array.isArray(data.players) ? data.players : []);
    setNewName(typeof data.newName === "string" ? data.newName : "");
    setImposterCount(typeof data.imposterCount === "number" ? data.imposterCount : 1);
    setImposterSet(new Set(Array.isArray(data.imposterIndices) ? data.imposterIndices : []));
    setSecretWord(typeof data.secretWord === "string" ? data.secretWord : "");
    setSecretWordHints(Array.isArray(data.secretWordHints) ? data.secretWordHints.filter((x) => typeof x === "string") : []);
    setCurrent(typeof data.current === "number" ? data.current : 0);
    setRevealed(Boolean(data.revealed));
    setCrewWordRevealed(Boolean(data.crewWordRevealed));
    setVotes(data.votes && typeof data.votes === "object" ? data.votes : {});
    setVoteSelection(Array.isArray(data.voteSelection) ? data.voteSelection : []);
    setEliminatedIndices(Array.isArray(data.eliminatedIndices) ? data.eliminatedIndices : []);
    setVoteTie(Boolean(data.voteTie));
    setFinalCrewWins(data.finalCrewWins === true || data.finalCrewWins === false ? data.finalCrewWins : null);
    setShowImposterReveal(Boolean(data.showImposterReveal));
  }, []);

  const tallyVotes = (voteMap: Record<number, number[]>) => {
    const tally: Record<number, number> = {};
    for (let i = 0; i < players.length; i++) tally[i] = 0;
    Object.entries(voteMap).forEach(([voterStr, targets]) => {
      const voter = Number(voterStr);
      const list = Array.isArray(targets) ? targets : [targets];
      list.forEach((t) => {
        if (t !== voter) tally[t] = (tally[t] ?? 0) + 1;
      });
    });
    return tally;
  };

  /**
   * Rank players by votes (desc, then index). Let k = number of imposters.
   * cutoff = vote count of the k-th player in that order.
   * Eliminate every player with votes >= cutoff (so ties at rank k all go out).
   * If the k-th cutoff is 0, eliminate everyone who received at least one vote instead.
   */
  const finalizeVoteRound = (nextVotes: Record<number, number[]>) => {
    const tally = tallyVotes(nextVotes);
    const n = players.length;
    const k = Math.max(1, imposterSet.size);
    const sortedFull = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
      const d = tally[b] - tally[a];
      if (d !== 0) return d;
      return a - b;
    });
    const maxVotes = sortedFull.reduce((m, i) => Math.max(m, tally[i]), 0);
    if (maxVotes === 0) {
      setVoteTie(true);
      setEliminatedIndices([]);
      setStep("after_vote");
      return;
    }
    const rankIdx = Math.min(k - 1, n - 1);
    const cutoffVotes = tally[sortedFull[rankIdx]];
    const eliminated =
      cutoffVotes > 0 ? sortedFull.filter((i) => tally[i] >= cutoffVotes) : sortedFull.filter((i) => tally[i] > 0);
    setVoteTie(false);
    setEliminatedIndices(eliminated);
    setStep("after_vote");
  };

  /** Single-vote mode: one tap submits. Multi: call after submit with `voteSelection`. */
  const submitCurrentVotes = (targets: number[]) => {
    const uniq = Array.from(new Set(targets))
      .filter((i) => i !== current)
      .sort((a, b) => a - b);
    if (uniq.length !== votesPerVoter) return;
    const nextVotes = { ...votes, [current]: uniq };
    setVotes(nextVotes);
    setVoteSelection([]);
    if (current + 1 < players.length) {
      setCurrent((c) => c + 1);
      return;
    }
    finalizeVoteRound(nextVotes);
  };

  const toggleVotePick = (targetIdx: number) => {
    if (targetIdx === current) return;
    setVoteSelection((prev) => {
      if (prev.includes(targetIdx)) return prev.filter((x) => x !== targetIdx);
      if (prev.length >= votesPerVoter) return prev;
      return [...prev, targetIdx].sort((a, b) => a - b);
    });
  };

  const resolveAfterVote = () => {
    if (voteTie || eliminatedIndices.length === 0) {
      setFinalCrewWins(null);
      setStep("outcome");
      return;
    }
    if (eliminatedIndices.some((i) => imposterSet.has(i))) {
      setStep("imposter_guess");
      return;
    }
    setFinalCrewWins(false);
    setStep("outcome");
  };

  /** Host decides whether the imposter’s verbal guess matched the secret word. */
  const resolveImposterGuess = (guessWasCorrect: boolean) => {
    setFinalCrewWins(!guessWasCorrect);
    setStep("outcome");
  };

  const resetGame = () => {
    setStep("lobby");
    setVotes({});
    setVoteSelection([]);
    setEliminatedIndices([]);
    setVoteTie(false);
    setFinalCrewWins(null);
    setShowImposterReveal(false);
    setSecretWord("");
    setSecretWordHints([]);
    setCrewWordRevealed(false);
  };

  if (step === "lobby") {
    const digitLabels = [
      t("party.playImposter.imposterDigitOne"),
      t("party.playImposter.imposterDigitTwo"),
      t("party.playImposter.imposterDigitThree"),
    ];
    return (
      <Box sx={{ pb: 17.5 }}>
        <PartyTopBar title={t("party.playImposter.lobbyTitleBar")} onBack={() => navigate("/party/detail/imposter")} />
        <Stack spacing={1} sx={{ px: 2.5, pt: 0.5 }}>
          <Typography className="party-display" sx={{ fontSize: 34, lineHeight: 0.95, color: partyPal.ink }}>
            {t("party.playImposter.lobbyTitle")}
          </Typography>
          <Typography sx={{ color: partyPal.inkDim, fontSize: 14 }}>{t("party.playImposter.lobbyHint")}</Typography>
        </Stack>

        <PartyCastSourceToggle
          nativeTvSupported={nativeTvCast}
          castByQr={castByQr}
          onChange={setCastByQr}
          partyPal={partyPal}
        />

        {isTrialMode && (
          <Box sx={{ px: 2.5, pt: 1 }}>
            <PartyTrialSetupBanner />
          </Box>
        )}

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ px: 2.5, pt: 1 }}>
          {players.map((name, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                py: 1,
                pl: 1.75,
                pr: 1.25,
                borderRadius: 999,
                bgcolor: partyPal.inkFaint,
                color: partyPal.ink,
                fontFamily: "var(--font-display)",
                fontWeight: 700,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
              <IconButton
                type="button"
                size="small"
                onClick={() => removePlayer(i)}
                aria-label={t("common.delete")}
                sx={{
                  width: 22,
                  height: 22,
                  minWidth: 22,
                  p: 0,
                  bgcolor: "rgba(255,255,255,.08)",
                  color: partyPal.ink,
                  fontSize: 14,
                }}
              >
                ×
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ px: 2.5, pt: 1.75 }}>
          <TextField
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => handlePartyRosterNameEnter(e, addPlayer)}
            placeholder={t("party.playImposter.playerPlaceholder")}
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "rgba(255,255,255,0.05)",
                color: partyPal.ink,
                borderRadius: "14px",
                fontFamily: "var(--font-body)",
                "& fieldset": { borderColor: partyPal.inkFaint },
              },
            }}
          />
          <Button type="button" variant="outlined" className="party-btn party-btn-paper" onClick={addPlayer} sx={{ px: 2.25, py: 1.5, flexShrink: 0 }}>
            {t("party.playImposter.addPlayer")}
          </Button>
        </Stack>

        <Box sx={{ px: 2.5, pt: 1 }}>
          <PartySavedRosterSetupSection
            api={savedRoster}
            variant="players"
            currentPlayers={players}
            onQuickAddPlayer={(name) => {
              setPlayers((p) => [...p, name]);
              savedRoster.recordPlayerAdded(name);
            }}
          />
        </Box>

        <Box sx={{ px: 2.5, pt: 2 }}>
          <Typography className="party-mono" sx={{ fontSize: 11, color: partyPal.inkDim, letterSpacing: "0.1em", mb: 1 }}>
            {t("party.playImposter.imposterCountLabel")}
          </Typography>
          <Stack direction="row" spacing={1}>
            {[1, 2, 3].map((n) => (
              <Button
                key={n}
                type="button"
                onClick={() => setImposterCount(n)}
                sx={{
                  flex: 1,
                  py: 2.25,
                  borderRadius: 2,
                  bgcolor: imposterCount === n ? partyPal.hot : "rgba(255,255,255,0.04)",
                  color: imposterCount === n ? "#fff" : partyPal.ink,
                  fontFamily: "var(--font-display)",
                  fontWeight: 900,
                  fontSize: 24,
                  border: imposterCount === n ? "none" : `1.5px solid ${partyPal.inkFaint}`,
                }}
              >
                {digitLabels[n - 1]}
              </Button>
            ))}
          </Stack>
        </Box>

        {!isTrialMode && startMut.isError && (
          <Typography sx={{ px: 2.5, color: partyPal.hot, fontSize: 14 }}>
            {(startMut.error as any)?.response?.status === 402
              ? t("party.playImposter.errorInsufficientCredits")
              : (startMut.error as any)?.response?.data?.detail || t("party.playImposter.errorStartRound")}
          </Typography>
        )}

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            maxWidth: 520,
            mx: "auto",
            px: 2.5,
            py: 1.75,
            pb: "calc(20px + env(safe-area-inset-bottom, 0px))",
            background: `linear-gradient(to top, ${partyPal.bg} 60%, transparent)`,
            zIndex: 50,
          }}
        >
          {showResumePrompt && cacheEnvelope ? (
            <PartyPlayResumePrompt
              savedAtIso={cacheEnvelope.savedAt}
              onContinue={() => {
                applyImposterCache(cacheEnvelope.data);
                setResumeDismissed(true);
              }}
              onNewGame={() => {
                clearPartyGameCache(CACHE_SHELL);
                setResumeDismissed(true);
              }}
            />
          ) : (
            <>
              <Button
                type="button"
                disabled={players.length < 3 || (!isTrialMode && startMut.isPending)}
                className="party-btn party-btn-hot"
                fullWidth
                onClick={() => (isTrialMode ? startImposterTrial() : goLobbyToCategory())}
                sx={{ opacity: players.length < 3 ? 0.5 : 1 }}
              >
                {isTrialMode ? t("party.trial.continueSampleCta") : t("party.playImposter.pickCategoryCta", { count: players.length })}
              </Button>
              {players.length < 3 && (
                <Typography sx={{ mt: 1.25, fontSize: 13, color: partyPal.inkDim, textAlign: "center" }}>
                  {t("party.playImposter.needThreePlayers")}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (step === "pick_category") {
    return (
      <Box sx={{ px: 2.5, pb: 15 }}>
        <PartyTopBar title={t("party.playImposter.categoryTitleBar")} onBack={() => setStep("lobby")} />
        <Typography className="party-display" sx={{ fontSize: 28, mb: 1, color: partyPal.ink }}>
          {t("party.playImposter.categoryHeading")}
        </Typography>
        <Typography sx={{ color: partyPal.inkDim, fontSize: 14, mb: 2 }}>{t("party.playImposter.categoryHint")}</Typography>
        {categoriesQ.isLoading && (
          <Typography sx={{ color: partyPal.inkDim }}>{t("common.loading")}</Typography>
        )}
        {categoriesQ.isError && (
          <Typography sx={{ color: partyPal.hot }}>{t("party.playImposter.categoriesLoadError")}</Typography>
        )}
        <Stack spacing={1.25}>
          {(categoriesQ.data ?? []).map((c) => (
            <Button
              key={c.id}
              type="button"
              disabled={startMut.isPending}
              onClick={() =>
                startMut.mutate({
                  categoryId: c.id,
                  category_names: [modeCategoryPrimaryName(c)],
                })
              }
              className="party-btn party-btn-paper"
              sx={{ textAlign: "start", py: 2, px: 2, fontSize: 16, justifyContent: "flex-start" }}
            >
              {categoryLabel(c)}
            </Button>
          ))}
        </Stack>
        <Button
          type="button"
          variant="text"
          onClick={() => setSuggestCategoryOpen(true)}
          sx={{ mt: 1, color: partyPal.hot, fontWeight: 800, textTransform: "none" }}
        >
          {t("party.suggestCategory.cta")}
        </Button>
        <PartySuggestCategoryDrawer
          open={suggestCategoryOpen}
          onClose={() => setSuggestCategoryOpen(false)}
          gameKey="imposter"
          partyPal={partyPal}
        />
      </Box>
    );
  }

  if (step === "reveal") {
    const advance = () => {
      setRevealed(false);
      setCrewWordRevealed(false);
      if (current + 1 >= players.length) {
        setStep("questions");
      } else setCurrent(current + 1);
    };
    const rtlFlag = Boolean(i18n.language?.startsWith("ar"));
    const castPayloadBase = (): Pick<PartyCastPayloadV1, "v" | "shell" | "rtl"> => ({
      v: 1,
      shell: "imposter",
      rtl: rtlFlag,
    });
    return (
      <Stack sx={{ height: "100dvh", p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <IconButton
            type="button"
            onClick={() => navigate("/party/detail/imposter")}
            aria-label={t("common.close")}
            sx={{
              border: `1.5px solid ${partyPal.inkFaint}`,
              color: partyPal.ink,
              width: 40,
              height: 40,
            }}
          >
            ✕
          </IconButton>
          <Typography className="party-mono" sx={{ color: partyPal.inkDim, fontSize: 12, letterSpacing: "0.1em" }}>
            {t("party.playImposter.revealProgress", { current: current + 1, total: players.length })}
          </Typography>
          <Box sx={{ width: 40 }} />
        </Stack>

        <Stack flex={1} justifyContent="center" alignItems="center" textAlign="center" sx={{ width: "100%" }}>
          {!revealed ? (
            <Box
              className="party-anim-fade-scale-in"
              sx={{
                width: "100%",
                maxWidth: 360,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography className="party-mono" sx={{ color: partyPal.inkDim, fontSize: 12, letterSpacing: "0.2em", mb: 1.25, textAlign: "center", width: "100%" }}>
                {t("party.playImposter.revealPassTo")}
              </Typography>
              <Typography className="party-display" sx={{ fontSize: 64, mb: 3, color: partyPal.ink, textAlign: "center", width: "100%" }}>
                {players[current]}
              </Typography>
              <PartyPressToReveal label={t("party.playImposter.pressHold")} onReveal={() => setRevealed(true)} />
              <Typography sx={{ color: partyPal.inkDim, mt: 2.5, fontSize: 13, textAlign: "center", width: "100%" }}>{t("party.playImposter.revealHoldHint")}</Typography>
            </Box>
          ) : isImposter(current) ? (
            castByQr ? (
              <Box className="party-anim-fade-scale-in" sx={{ width: "100%", maxWidth: 400, mx: "auto" }}>
                <PartyCastQrPanel
                  partyPal={partyPal}
                  url={buildPartyCastRevealUrl("imposter", {
                    ...castPayloadBase(),
                    layout: "imposter_role",
                    h1: t("party.playImposter.imposterRoleLabel"),
                    h2: t("party.playImposter.imposterRoleTitle"),
                    h3: t("party.playImposter.imposterRoleBody"),
                  })}
                />
                <Button type="button" onClick={advance} className="party-btn party-btn-hot" fullWidth sx={{ mt: 2 }}>
                  {t("party.playImposter.nextUnderstood")}
                </Button>
              </Box>
            ) : (
            <Box className="party-anim-fade-scale-in" sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
              <Box
                className="party-kan-card"
                sx={
                  {
                    ["--card-bg" as string]: "linear-gradient(160deg, #2A0B1A, #1A0520)",
                    color: partyPal.ink,
                    py: 5,
                    px: 3,
                    textAlign: "center",
                  } as React.CSSProperties
                }
              >
                <PartyMascot shellId="imposter" size={110} />
                <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: partyPal.hot, mt: 1 }}>
                  {t("party.playImposter.imposterRoleLabel")}
                </Typography>
                <Typography className="party-display" sx={{ fontSize: 56, color: partyPal.hot, mt: 0.75 }}>
                  {t("party.playImposter.imposterRoleTitle")}
                </Typography>
                <Typography sx={{ mt: 1.5, color: partyPal.inkDim, fontSize: 14, maxWidth: "26ch", mx: "auto" }}>
                  {t("party.playImposter.imposterRoleBody")}
                </Typography>
              </Box>
              <Button type="button" onClick={advance} className="party-btn party-btn-hot" fullWidth sx={{ mt: 2.75 }}>
                {t("party.playImposter.nextUnderstood")}
              </Button>
            </Box>
            )
          ) : !crewWordRevealed ? (
            castByQr ? (
              <Box className="party-anim-fade-scale-in" sx={{ width: "100%", maxWidth: 400, mx: "auto", alignItems: "center" }}>
                <Typography className="party-mono" sx={{ color: partyPal.inkDim, fontSize: 12, letterSpacing: "0.2em", mb: 1.25, textAlign: "center", width: "100%" }}>
                  {t("party.playImposter.revealPassTo")}
                </Typography>
                <Typography className="party-display" sx={{ fontSize: 56, mb: 2, color: partyPal.ink, textAlign: "center", width: "100%" }}>
                  {players[current]}
                </Typography>
                <PartyCastQrPanel
                  partyPal={partyPal}
                  url={buildPartyCastRevealUrl("imposter", {
                    ...castPayloadBase(),
                    layout: "imposter_word",
                    h1: t("party.playImposter.crewRoleLabel"),
                    h2: t("party.playImposter.crewRoleTitle"),
                    h3: t("party.playImposter.crewRoleBody"),
                    w: secretWord,
                    ...(secretWordHints.length > 0 ? { hints: secretWordHints } : {}),
                  })}
                />
                <Button
                  type="button"
                  onClick={() => setCrewWordRevealed(true)}
                  className="party-btn party-btn-hot"
                  fullWidth
                  sx={{ mt: 2, maxWidth: 360 }}
                >
                  {t("party.cast.continueAfterQr")}
                </Button>
              </Box>
            ) : (
              <Box className="party-anim-fade-scale-in" sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
                <Box
                  className="party-kan-card"
                  sx={
                    {
                      ["--card-bg" as string]: partyPal.paper,
                      color: partyPal.paperInk,
                      py: 4,
                      px: 3,
                      textAlign: "center",
                    } as React.CSSProperties
                  }
                >
                  <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: `color-mix(in oklab, ${partyPal.paperInk} 55%, transparent)` }}>
                    {t("party.playImposter.crewRoleLabel")}
                  </Typography>
                  <Typography className="party-display" sx={{ fontSize: 40, mt: 1.25, color: partyPal.paperInk }}>
                    {t("party.playImposter.crewRoleTitle")}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.75,
                      fontSize: 14,
                      color: `color-mix(in oklab, ${partyPal.paperInk} 72%, transparent)`,
                      lineHeight: 1.5,
                      maxWidth: "30ch",
                      mx: "auto",
                    }}
                  >
                    {t("party.playImposter.crewRoleBody")}
                  </Typography>
                </Box>
                <Stack alignItems="center" sx={{ mt: 2.5 }}>
                  <PartyPressToReveal label={t("party.playImposter.pressHoldWord")} onReveal={() => setCrewWordRevealed(true)} />
                </Stack>
              </Box>
            )
          ) : castByQr ? (
            <Box className="party-anim-fade-scale-in" sx={{ width: "100%", maxWidth: 400, mx: "auto" }}>
              <Button type="button" onClick={advance} className="party-btn party-btn-hot" fullWidth sx={{ mt: 2 }}>
                {t("party.playImposter.nextUnderstood")}
              </Button>
            </Box>
          ) : (
            <Box className="party-anim-fade-scale-in" sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}>
              <Box
                className="party-kan-card"
                sx={
                  {
                    ["--card-bg" as string]: partyPal.paper,
                    color: partyPal.paperInk,
                    py: 5,
                    px: 3,
                    textAlign: "center",
                  } as React.CSSProperties
                }
              >
                <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: `color-mix(in oklab, ${partyPal.paperInk} 60%, transparent)` }}>
                  {t("party.playImposter.secretWordLabel")}
                </Typography>
                <Typography className="party-display" sx={{ fontSize: 68, mt: 1.25, color: partyPal.paperInk }}>
                  {secretWord}
                </Typography>
                {secretWordHints.length > 0 ? (
                  <Box sx={{ mt: 2.5, width: "100%", maxWidth: "min(100%, 40ch)", mx: "auto" }}>
                    <Typography
                      className="party-mono"
                      sx={{
                        fontSize: 10,
                        opacity: 0.55,
                        mb: 0.75,
                        textAlign: "center",
                        color: `color-mix(in oklab, ${partyPal.paperInk} 72%, transparent)`,
                      }}
                    >
                      {t("party.playImposter.hintsHeading")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 1.25,
                        rowGap: 0.75,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {secretWordHints.map((line, hi) => (
                        <Typography
                          key={`${hi}:${line}`}
                          component="span"
                          sx={{ fontSize: 15, fontWeight: 700, color: partyPal.paperInk, lineHeight: 1.4 }}
                        >
                          · {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
              <Button type="button" onClick={advance} className="party-btn party-btn-hot" fullWidth sx={{ mt: 2.75 }}>
                {t("party.playImposter.nextUnderstood")}
              </Button>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  }

  if (step === "questions") {
    return (
      <Box sx={{ px: 2.5, pb: 15 }}>
        <PartyTopBar title={t("party.playImposter.questionsTitleBar")} onBack={() => setStep("reveal")} />
        <Typography className="party-display" sx={{ fontSize: 30, mb: 1, color: partyPal.ink }}>
          {t("party.playImposter.questionsHeading")}
        </Typography>
        <Typography sx={{ color: partyPal.inkDim, fontSize: 14, mb: 2 }}>{t("party.playImposter.questionsHint")}</Typography>
        <Stack spacing={1.25}>
          {questionPairs.map((q, i) => (
            <Box
              key={i}
              sx={{
                py: 1.75,
                px: 2,
                borderRadius: 2,
                border: `1.5px solid ${partyPal.inkFaint}`,
                bgcolor: "rgba(255,255,255,0.04)",
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 17,
              }}
            >
              <Typography component="span" sx={{ color: partyPal.hot }}>
                {q.asker}
              </Typography>
              <Typography component="span" sx={{ color: partyPal.inkDim, mx: 1 }}>
                ←
              </Typography>
              <Typography component="span" sx={{ color: partyPal.ink }}>
                {q.target}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ mt: 3 }}>
          <Button
            type="button"
            className="party-btn party-btn-hot"
            fullWidth
            onClick={() => {
              setCurrent(0);
              setVotes({});
              setVoteSelection([]);
              setEliminatedIndices([]);
              setVoteTie(false);
              setStep("vote");
            }}
          >
            {t("party.playImposter.questionsToVote")}
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === "vote") {
    const multi = votesPerVoter > 1;
    const pickLabel =
      votesPerVoter === 1
        ? t("party.playImposter.votePickSingle")
        : t("party.playImposter.votePickMulti", { count: votesPerVoter });
    return (
      <Box sx={{ px: 2.5, pb: 3 }}>
        <PartyTopBar title={t("party.playImposter.voteTitleBar")} onBack={() => setStep("questions")} />
        <Typography className="party-mono" sx={{ color: partyPal.inkDim, fontSize: 11, letterSpacing: "0.15em", mb: 0.5 }}>
          {t("party.playImposter.voteProgress", { current: current + 1, total: players.length })}
        </Typography>
        <Typography className="party-display" sx={{ fontSize: 32, lineHeight: 0.95, mb: 1, color: partyPal.ink }}>
          {players[current]}
        </Typography>
        <Typography sx={{ color: partyPal.inkDim, fontSize: 14, mb: 1.75, lineHeight: 1.45 }}>{pickLabel}</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
          {players.map((playerName, i) =>
            i === current ? null : (
              <Button
                key={i}
                type="button"
                onClick={() => (multi ? toggleVotePick(i) : submitCurrentVotes([i]))}
                sx={{
                  justifyContent: "flex-start",
                  border: multi && voteSelection.includes(i) ? `2px solid ${partyPal.hot}` : `1.5px solid ${partyPal.inkFaint}`,
                  bgcolor: multi && voteSelection.includes(i) ? "rgba(255,61,120,0.14)" : "rgba(255,255,255,0.04)",
                  color: partyPal.ink,
                  py: 2.75,
                  px: 1.75,
                  borderRadius: 2.5,
                  fontFamily: "var(--font-display)",
                  fontWeight: 800,
                  fontSize: 20,
                  textTransform: "none",
                }}
              >
                {playerName}
                {multi && voteSelection.includes(i) ? " ✓" : ""}
              </Button>
            )
          )}
        </Box>
        {multi && (
          <Button
            type="button"
            className="party-btn party-btn-hot"
            fullWidth
            disabled={voteSelection.length !== votesPerVoter}
            onClick={() => submitCurrentVotes(voteSelection)}
            sx={{ mt: 2, opacity: voteSelection.length === votesPerVoter ? 1 : 0.45 }}
          >
            {t("party.playImposter.voteSubmit", { had: voteSelection.length, need: votesPerVoter })}
          </Button>
        )}
      </Box>
    );
  }

  if (step === "after_vote") {
    return (
      <Box sx={{ p: 2.5 }}>
        <PartyTopBar
          title={t("party.playImposter.eliminationTitleBar")}
          onBack={() => {
            setVotes({});
            setVoteSelection([]);
            setCurrent(0);
            setEliminatedIndices([]);
            setVoteTie(false);
            setStep("vote");
          }}
        />
        {voteTie || eliminatedIndices.length === 0 ? (
          <Typography sx={{ fontSize: 16, color: partyPal.ink, lineHeight: 1.6 }}>{t("party.playImposter.eliminationNoVotes")}</Typography>
        ) : (
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.5, color: partyPal.ink }}>
            {t("party.playImposter.eliminationList", { count: eliminatedIndices.length })}{" "}
            <Box component="span" sx={{ color: partyPal.hot }}>
              {eliminatedIndices.map((i) => players[i]).join("، ")}
            </Box>
          </Typography>
        )}
        <Button type="button" className="party-btn party-btn-hot" fullWidth sx={{ mt: 3 }} onClick={resolveAfterVote}>
          {t("party.playImposter.continue")}
        </Button>
      </Box>
    );
  }

  if (step === "imposter_guess") {
    return (
      <Box sx={{ p: 2.5 }}>
        <PartyTopBar title={t("party.playImposter.guessTitleBar")} onBack={() => setStep("after_vote")} />
        <Typography sx={{ mb: 2, fontSize: 16, lineHeight: 1.55, color: partyPal.ink }}>{t("party.playImposter.guessIntro")}</Typography>
        <Box
          className="party-kan-card"
          sx={{ p: 2, mb: 2, ["--card-bg" as string]: partyPal.paper, color: partyPal.paperInk } as React.CSSProperties}
        >
          <Typography className="party-mono" sx={{ fontSize: 11, color: partyPal.paperInk }}>
            {t("party.playImposter.hostWordLabel")}
          </Typography>
          <Typography className="party-display" sx={{ fontSize: 36, mt: 0.75, color: partyPal.paperInk }}>
            {secretWord}
          </Typography>
          {secretWordHints.length > 0 ? (
            <Box sx={{ mt: 2, width: "100%" }}>
              <Typography className="party-mono" sx={{ fontSize: 10, opacity: 0.55, mb: 0.75 }}>
                {t("party.playImposter.hintsHeading")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 1,
                  rowGap: 0.5,
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {secretWordHints.map((line, hi) => (
                  <Typography key={`${hi}:${line}`} component="span" sx={{ fontSize: 14, fontWeight: 700, color: partyPal.paperInk }}>
                    · {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
        <Stack spacing={1.25}>
          <Button type="button" className="party-btn party-btn-hot" fullWidth onClick={() => resolveImposterGuess(true)}>
            {t("party.playImposter.guessCorrectCta")}
          </Button>
          <Button type="button" className="party-btn party-btn-paper" fullWidth onClick={() => resolveImposterGuess(false)}>
            {t("party.playImposter.guessWrongCta")}
          </Button>
        </Stack>
      </Box>
    );
  }

  if (step === "outcome" && finalCrewWins !== null) {
    const crew = finalCrewWins;
    const revealedImposterNames = players.filter((_, i) => imposterSet.has(i)).join("، ");
    return (
      <Stack sx={{ height: "100dvh", p: 2.5, position: "relative", overflow: "hidden" }}>
        <PartyConfettiCards count={8} />
        <Stack flex={1} justifyContent="center" alignItems="center" textAlign="center" sx={{ zIndex: 2 }}>
          <PartyMascot shellId={crew ? "category" : "imposter"} size={130} />
          <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.2em", color: partyPal.inkDim, mt: 1.75 }}>
            {t("party.playImposter.outcomeLabel")}
          </Typography>
          <Typography className="party-display" sx={{ fontSize: 56, lineHeight: 0.9, mt: 1, color: partyPal.ink }}>
            {crew ? t("party.playImposter.outcomeCrewWin") : t("party.playImposter.outcomeImposterWin")}
          </Typography>
          <Typography sx={{ color: partyPal.inkDim, mt: 1.75, fontSize: 15, maxWidth: 280 }}>
            {t("party.playImposter.wordWas")}{" "}
            <Box component="strong" sx={{ color: partyPal.gold }}>
              {secretWord}
            </Box>
          </Typography>
          {secretWordHints.length > 0 ? (
            <Box sx={{ mt: 1.5, textAlign: "center", maxWidth: 360, px: 1 }}>
              <Typography className="party-mono" sx={{ fontSize: 10, opacity: 0.55, mb: 0.5 }}>
                {t("party.playImposter.hintsHeading")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 1,
                  rowGap: 0.35,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {secretWordHints.map((line, hi) => (
                  <Typography key={`${hi}:${line}`} component="span" sx={{ fontSize: 14, color: partyPal.ink, lineHeight: 1.35 }}>
                    · {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}
          <FormControlLabel
            sx={{ mt: 2.5, color: partyPal.inkDim, fontSize: 14 }}
            control={<Checkbox checked={showImposterReveal} onChange={(e) => setShowImposterReveal(e.target.checked)} sx={{ color: partyPal.inkDim }} />}
            label={t("party.playImposter.revealImposterNames")}
          />
          {showImposterReveal && (
            <Typography sx={{ mt: 1, fontSize: 15, color: partyPal.ink }}>{revealedImposterNames}</Typography>
          )}
        </Stack>
        <Stack spacing={1.25} sx={{ zIndex: 2 }}>
          <PartyShareResultButton
            className="party-btn party-btn-paper"
            sx={{ width: "100%" }}
            model={{
              shellId: "imposter",
              modeTitle: shareModeTitle,
              resultTitle: t("party.playImposter.outcomeLabel"),
              hero: crew ? t("party.playImposter.outcomeCrewWin") : t("party.playImposter.outcomeImposterWin"),
              sub: `${t("party.playImposter.wordWas")} ${secretWord}`,
              meta: showImposterReveal && revealedImposterNames ? [t("party.playImposter.revealImposterNames"), revealedImposterNames] : undefined,
            }}
          />
          <Stack direction="row" spacing={1.25}>
          <Button type="button" onClick={resetGame} className="party-btn party-btn-ghost" sx={{ flex: 1 }}>
            {t("party.playImposter.newRound")}
          </Button>
          <Button type="button" onClick={() => navigate("/")} className="party-btn party-btn-hot" sx={{ flex: 1 }}>
            {t("party.playImposter.home")}
          </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (step === "outcome" && finalCrewWins === null) {
    return (
      <Box sx={{ p: 2.5 }}>
        <PartyTopBar title={t("party.playImposter.outcomeTieTitleBar")} onBack={() => setStep("after_vote")} />
        <Typography sx={{ fontSize: 16, mb: 2, color: partyPal.ink }}>{t("party.playImposter.outcomeTieBody")}</Typography>
        <Stack direction="row" spacing={1.25}>
          <Button
            type="button"
            className="party-btn party-btn-ghost"
            sx={{ flex: 1 }}
            onClick={() => {
              setVotes({});
              setVoteSelection([]);
              setEliminatedIndices([]);
              setVoteTie(false);
              setCurrent(0);
              setStep("vote");
            }}
          >
            {t("party.playImposter.revote")}
          </Button>
          <Button type="button" className="party-btn party-btn-hot" sx={{ flex: 1 }} onClick={() => navigate("/")}>
            {t("party.playImposter.home")}
          </Button>
        </Stack>
      </Box>
    );
  }

  return null;
}
